import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Dropdown, Label } from '@heroui/react';
import { reactive, useHandleCoreEvent } from '@livefree/applications';
import { RecentViewEvent } from '@livefree/runtime';
import { hostRuntime } from '@/core';

const findCurrentNavItem = pathname => {
  const app = hostRuntime
    .getApps()
    .find(a => pathname === a.baseUrl || pathname.startsWith(`${a.baseUrl}/`));
  if (!app) return null;

  const navigation = app.navigation();
  if (!navigation?.isVisible) return null;

  const leaves = [];
  const flatten = (items, group) => {
    for (const item of items ?? []) {
      if (item.isVisible === false) continue;
      if (item.items) flatten(item.items, item);
      else if (item.route) leaves.push({ item, group });
    }
  };
  flatten(navigation.items);

  let best = null;
  for (const leaf of leaves) {
    // A root leaf ('/') would otherwise build `${baseUrl}/` and match nothing below it.
    const route = `${app.baseUrl}${leaf.item.route}`.replace(/\/$/, '');
    if (pathname !== route && !pathname.startsWith(`${route}/`)) continue;
    if (!best || route.length > best.route.length) best = { ...leaf, route };
  }
  if (!best) return null;

  return {
    route: best.route,
    title: best.item.title,
    app: best.group?.title ?? app.description.title,
    iconClass: best.item.icon ?? app.description.iconClass,
    appId: app.id,
  };
};

const RecentViewsBarView = ({ userId, services, data }) => {
  const { views } = data;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const announcements = useRef(new Map());
  const onAnnounce = useRef(null);

  const handleRecentViewEvent = useCallback(evt => {
    const detail = evt.detail;
    if (!detail?.pathname) return;
    announcements.current.set(detail.pathname, detail);
    onAnnounce.current?.(detail.pathname);
  }, []);
  useHandleCoreEvent(RecentViewEvent, handleRecentViewEvent);

  useEffect(() => {
    if (userId == null) return;

    for (const key of announcements.current.keys()) {
      if (key !== pathname) announcements.current.delete(key);
    }

    // Only entity views land in recents: an exact navigation route is a
    // top-level list, and deeper routes count once the app announces a title.
    const current = findCurrentNavItem(pathname);
    if (!current || pathname === current.route) return;

    const trackAnnounced = () => {
      const announced = announcements.current.get(pathname);
      if (!announced?.title) return;
      services.recentViews.track({
        userId,
        ...current,
        route: announced.route ?? pathname,
        title: announced.title,
        timestamp: Date.now(),
      });
      onAnnounce.current = null;
    };

    onAnnounce.current = announcedPath => {
      if (announcedPath === pathname) trackAnnounced();
    };
    trackAnnounced();

    return () => {
      onAnnounce.current = null;
    };
  }, [pathname, userId, services]);

  // Resolving a route against the nav tree rebuilds each app's navigation, which
  // re-reads the token and the user's policies. Keep it off the render path: the
  // stored routes only change when the views query re-emits.
  const entityViews = useMemo(
    () => views.filter(view => findCurrentNavItem(view.route)?.route !== view.route),
    [views],
  );

  const chip = view => (
    <>
      {view.iconClass && <i className={`${view.iconClass} text-muted`} />}
      {view.app && <span className="text-muted">{view.app}</span>}
      <span>{view.title}</span>
    </>
  );

  return (
    <div className="sticky top-0 z-20 flex h-9 flex-none items-center gap-2 px-5">
      <i className="ri-history-line text-muted" />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">Recent</span>

      {/* From md up: the chip strip. */}
      <div className="hidden min-w-0 flex-1 items-center gap-1 overflow-x-hidden md:flex">
        {entityViews.map(view => (
          <button
            key={view.route}
            type="button"
            onClick={() => navigate(view.route)}
            className="inline-flex h-6 flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border bg-background-secondary px-2.5 text-xs transition-colors hover:bg-overlay"
          >
            {chip(view)}
          </button>
        ))}
      </div>

      {/* Below md: the same entries in a menu, so the row never scrolls sideways. */}
      {entityViews.length > 0 && (
        <div className="flex flex-1 items-center justify-end md:hidden">
          <Dropdown>
            <Button
              size="sm"
              variant="tertiary"
              className="h-6 gap-1.5 rounded-full px-2.5 text-xs"
            >
              {entityViews.length}
              <i className="ri-arrow-down-s-line" />
            </Button>
            <Dropdown.Popover placement="bottom end">
              <Dropdown.Menu
                aria-label="Recent views"
                items={entityViews}
                onAction={route => navigate(String(route))}
              >
                {view => (
                  <Dropdown.Item key={view.route} id={view.route} textValue={view.title}>
                    <div className="flex items-center gap-2">
                      {view.iconClass && <i className={`${view.iconClass} text-muted`} />}
                      <div className="flex flex-col">
                        <Label>{view.title}</Label>
                        {view.app && <span className="text-muted text-xs">{view.app}</span>}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      )}
    </div>
  );
};

export const RecentViewsBar = reactive.component(RecentViewsBarView, {
  name: 'RecentViewsBar',
  queries: props => [
    {
      name: 'views',
      execute: db =>
        props.userId == null
          ? Promise.resolve([])
          : db.recent_views
              .where({ userId: props.userId })
              .sortBy('timestamp')
              .then(views => views.reverse()),
      defaultValue: [],
      deps: [props.userId],
    },
  ],
});
